import type { User } from "../slices/authSlice"
import CurrencySelector from "../components/layout/CurrencySelector"
import { useEffect, useState } from "react"
import { SpinnerText } from "../components/ui/Spinner"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { FiCreditCard, FiRefreshCw, FiSave } from "react-icons/fi"
import AppLayout from "../layout/AppLayout"
import ProgressBarV2 from "../components/ui/ProgressBar_v2"
import ReferenceNumberSection from "../components/trademark/reference_number/ReferenceNumberSection"
import FeesBoxV2 from "../components/trademark/sidebar/FeesBox_v2"
import ServiceDescriptions from "../components/ui/ServiceDescriptions"
import { FilingSidebarAccordion } from "../components/ui/FilingSidebarAccordion"
import SignatureSection from "../components/trademark/signature/SignatureSection"
import ApplicantAndRepresentativeWrapper from "../components/trademark/applicant/ApplicantAndRepresentativeWrapper"
import { resetForm } from "../hooks/resetForm"
import StatusToast from "../components/ui/StatusToast"
import SectionFieldError from "../components/ui/SectionFieldError"
import StatusBadge from "../components/ui/StatusBadge"
import DesignsSection, {
  SavedDesignCard,
  mapLoadedDesignToCard
} from "../components/design/DesignsSection"
import { saveDesignDraft } from "../services/saveDesignDraftService"
import {
  ApiFieldError,
  formatApiError,
  getApiFieldErrors
} from "../utils/formatApiError"
import {
  getDesignApplicationById,
  updateDesignApplicationStatus,
  validateDesignApplication
} from "../services/designApplicationService"
import { RootState } from "../store/store"
import { calculateDesignFeeBreakdown } from "../utils/applicationFees"


function getEntityId(value: any) {
  if (!value) return ""
  if (typeof value === "string") return value
  if (typeof value === "object") return String(value._id || value.id || "")
  return String(value)
}

function DesignProgressSection({
  complete
}: {
  complete: Record<string, boolean>
}) {
  return (
    <section className="sticky top-0 z-50 bg-white border-b border-black pb-6">
      <ProgressBarV2 complete={complete} />
    </section>
  )
}

export default function DesignFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isViewMode = location.pathname.includes("/view")
  const isAdmin = useSelector((state: RootState) => Boolean(state.auth.user?.isAdmin))
  const currentUserId = useSelector((state: RootState) => state.auth.user?.id || "")
  const [referenceNumber, setReferenceNumber] = useState("")
  const [hasDesign, setHasDesign] = useState(false)
  const [designIds, setDesignIds] = useState<string[]>([])
  const [designDrafts, setDesignDrafts] = useState<SavedDesignCard[]>([])
  const [orderingParty, setOrderingParty] = useState<Partial<User> | null | undefined>(undefined)
  const [draftId, setDraftId] = useState<string | null>(null)
  const [applicants, setApplicants] = useState<any[] | null>(null)
  const [ownerUserId, setOwnerUserId] = useState("")
  const [signature, setSignature] = useState<any | null>(null)
  const [signatureId, setSignatureId] = useState<string | null>(null)
  const [applicationStatus, setApplicationStatus] = useState<"DRAFT" | "VALIDATED" | "AWAITING_RELEASE" | "PAYMENT_PENDING" | "ERROR" | "PAID">("DRAFT")
  const [validationError, setValidationError] = useState("")
  const [fieldErrors, setFieldErrors] = useState<ApiFieldError[]>([])
  const [successMessage, setSuccessMessage] = useState("")
  const [savingDraft, setSavingDraft] = useState(false)
  const [loading, setLoading] = useState(Boolean(id))
  const isSubmitted =
    applicationStatus === "PAID" ||
    applicationStatus === "PAYMENT_PENDING" ||
    applicationStatus === "AWAITING_RELEASE"
  const isPaid = isViewMode || (!isAdmin && isSubmitted)
  const statusMustStayLocked =
    applicationStatus === "PAID" ||
    applicationStatus === "PAYMENT_PENDING" ||
    applicationStatus === "AWAITING_RELEASE" ||
    applicationStatus === "ERROR"

  const paymentEnabled =
    !isSubmitted &&
    !isPaid &&
    hasDesign &&
    designDrafts.length > 0 &&
    !!referenceNumber.trim() &&
    !!applicants?.length &&
    !!signature?.accepted
  const designFees = calculateDesignFeeBreakdown(designDrafts)

  const handleDesignChange = (value: boolean) => {
    setHasDesign(value)
  }

  const saveDesignApplicationDraft = async () => {
    if (isPaid) {
      throw new Error("Paid applications cannot be edited.")
    }

    const savedId = await saveDesignDraft({
      referenceNumber,
      designDrafts,
      existingDesignIds: designIds,
      applicants,
      signature,
      draftId,
      signatureId,
      onDraftSaved: setDraftId,
      onDesignsSaved: setDesignIds,
      onSignatureSaved: setSignatureId
    })

    const nextDraftId = savedId || draftId
    setDraftId(nextDraftId)

    if (nextDraftId && !statusMustStayLocked) {
      await updateDesignApplicationStatus(nextDraftId, "DRAFT").catch(() => {})
    }

    if (!statusMustStayLocked) {
      setApplicationStatus("DRAFT")
    }

    return nextDraftId
  }

  const handleSaveDesignApplication = async () => {
    if (savingDraft || isPaid) return

    try {
      setSavingDraft(true)
      await saveDesignApplicationDraft()
      setValidationError("")
      setFieldErrors([])
      setSuccessMessage("Design application draft saved successfully.")
    } catch (error) {
      console.error("Design application could not be saved:", error)
      setSuccessMessage("")
      setValidationError(
        formatApiError(
          error,
          "The design application could not be saved. Please reload the page and try again."
        )
      )
    } finally {
      setSavingDraft(false)
    }
  }

  const handleProceedToPayment = async () => {
    if (savingDraft || !paymentEnabled || isPaid) return

    try {
      setSavingDraft(true)
      const nextDraftId = await saveDesignApplicationDraft()

      if (!nextDraftId) {
        throw new Error("Draft could not be saved.")
      }

      await validateDesignApplication(nextDraftId)
      await updateDesignApplicationStatus(nextDraftId, "VALIDATED")
      setApplicationStatus("VALIDATED")
      setValidationError("")
      setFieldErrors([])
      setSuccessMessage("")
      navigate(`/design-applications/${nextDraftId}/payment`)
    } catch (error) {
      setFieldErrors(getApiFieldErrors(error))
      setSuccessMessage("")
      setValidationError(
        formatApiError(
          error,
          "The design application could not be validated. Please reload the page and try again."
        )
      )
      if (draftId) {
        await updateDesignApplicationStatus(draftId, "ERROR").catch(() => {})
      }
      setApplicationStatus("ERROR")
    } finally {
      setSavingDraft(false)
    }
  }

  const sectionErrors = (...fields: string[]) =>
    fieldErrors
      .filter(error =>
        fields.some(field =>
          error.field === field || error.field.startsWith(`${field}.`)
        )
      )
      .map(error => error.message)

  useEffect(() => {
    const designApplicationId = id

    if (!designApplicationId) {
      setLoading(false)
      return
    }

    async function load() {
      try {
        const app = await getDesignApplicationById(designApplicationId as string)
        const loadedDesigns = app.designs || []

        setOrderingParty(typeof app.userId === "object" ? app.userId : null)
        setDraftId(app._id)
        setOwnerUserId(getEntityId(app.userId) || currentUserId)
        setApplicationStatus(app.status || "DRAFT")
        setReferenceNumber(app.referenceNumber?.referenceNumber || "")
        setDesignIds(loadedDesigns.map((design: any) => design._id))
        setDesignDrafts(
          loadedDesigns.map((design: any, index: number) =>
            mapLoadedDesignToCard(design, index)
          )
        )
        setHasDesign(loadedDesigns.length > 0)
        setApplicants(app.applicants || [])

        if (app.signature) {
          setSignatureId(app.signature._id)
          setSignature({ ...app.signature, accepted: true })
        }
      } catch (error) {
        setValidationError(
          formatApiError(
            error,
            "The design application could not be loaded. Please reload the page and try again."
          )
        )
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [id, currentUserId])

  if (loading) {
    return (
      <AppLayout title="Design Application" wide>
        <div className="flex justify-center py-32 text-gray-600">
          <SpinnerText>Loading application...</SpinnerText>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout title="Design Application" wide>
      {validationError && (
        <StatusToast
          type="error"
          message={validationError}
          onClose={() => setValidationError("")}
        />
      )}

      {successMessage && (
        <StatusToast
          type="success"
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}

      {savingDraft && <StatusToast type="loading" message="Saving your design application…" />}

      <div className="sticky top-0 z-30 border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <DesignProgressSection
            complete={{
              designs: hasDesign,
              applicant: !!applicants?.length,
              signature: !!signature?.accepted,
              payment: isSubmitted
            }}
          />
        </div>
      </div>

      <div className="flex justify-center py-12 pb-28">
        <div className="grid grid-cols-1 gap-7 max-w-7xl w-full xl:grid-cols-[minmax(0,1fr)_18rem]">
          <div className="flex flex-col gap-10 p-5 sm:p-8 xl:p-12 bg-white border rounded-2xl shadow-sm">
            <ReferenceNumberSection
              value={referenceNumber}
              onChange={setReferenceNumber}
              disabled={isPaid}
            />

            <SectionFieldError
              messages={sectionErrors("designs")}
            />
            <DesignsSection
              onChange={handleDesignChange}
              onDraftsChange={setDesignDrafts}
              initialDesigns={designDrafts}
              disabled={isPaid}
            />

            <SectionFieldError messages={sectionErrors("applicants")} />
            <ApplicantAndRepresentativeWrapper
              orderingParty={orderingParty}
              onApplicantChange={setApplicants}
              initialData={applicants ?? []}
              disabled={isPaid}
              importSource="design"
              currentApplicationId={draftId}
              ownerUserId={ownerUserId || currentUserId}
            />

            <div id="signature">
              <SectionFieldError messages={sectionErrors("signature")} />
              <SignatureSection
                onChange={setSignature}
                initialData={signature}
                textVariant="design"
                disabled={isPaid}
              />
            </div>

            <div id="payment">
              <section className="pb-6"></section>
            </div>

          </div>

          <aside className="block">
            <div className="flex w-full flex-col gap-4 xl:sticky xl:top-[128px] xl:max-h-[calc(100vh-9rem)] xl:w-72 xl:overflow-y-auto xl:[scrollbar-width:none] xl:[&::-webkit-scrollbar]:hidden">
              <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white p-3 text-sm shadow-sm">
                <div className="mb-1 flex items-center justify-between gap-3 border-b border-gray-100 pb-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Status
                  </span>
                  <StatusBadge status={applicationStatus} />
                </div>
                {!isPaid && (
                  <>
                    <button
                      type="button"
                      onClick={handleSaveDesignApplication}
                      disabled={savingDraft}
                      className={`flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        savingDraft
                          ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
                          : "border-gray-300 bg-white text-dantzBlue hover:border-dantzBlue hover:bg-blue-50"
                      }`}
                    >
                      <FiSave className="h-4 w-4" aria-hidden="true" />
                      {savingDraft ? <SpinnerText>Saving draft...</SpinnerText> : "Save as draft"}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        resetForm()
                        window.dispatchEvent(new CustomEvent("resetAll"))
                      }}
                      disabled={savingDraft}
                      className={`flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        savingDraft
                          ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
                          : "border-gray-300 bg-white text-dantzBlue hover:border-dantzBlue hover:bg-blue-50"
                      }`}
                    >
                      <FiRefreshCw className="h-4 w-4" aria-hidden="true" />
                      Reset form
                    </button>

                    <button
                      type="button"
                      onClick={handleProceedToPayment}
                      disabled={!paymentEnabled || savingDraft}
                      className={`flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        paymentEnabled && !savingDraft
                          ? "bg-dantzOrange text-white hover:brightness-110 active:scale-[0.98]"
                          : "cursor-not-allowed bg-gray-300 text-gray-500"
                      }`}
                    >
                      <FiCreditCard className="h-4 w-4" aria-hidden="true" />
                      Proceed to payment
                    </button>
                  </>
                )}

                {isPaid && (
                  <p className="mt-2 text-xs text-gray-500">
                    This application has already been submitted and can no longer be edited.
                  </p>
                )}
              </div>
              <FilingSidebarAccordion>
                <FeesBoxV2
                  total={designFees.total}
                  serviceLines={designFees.serviceLines}
                  officialLines={designFees.officialLines}
                  subtotalServices={designFees.subtotalServices}
                  subtotalOfficial={designFees.subtotalOfficial}
                  ready={designDrafts.length > 0}
                  placeholderText="The price is shown once you have added a design."
                />
                <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
                  <CurrencySelector />
                </div>
                <ServiceDescriptions type="design" />
              </FilingSidebarAccordion>
            </div>
          </aside>
        </div>
      </div>

    </AppLayout>
  )
}
